import React from 'react';
import { ITableDataSource } from 'models/table';
import { Grid, Link, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { formatDate } from 'utils/date';
import { StyledTableCard, StyledTableCardItem } from '../../styles';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import { colors } from 'theme/colors';
import { IAttachment } from 'models/cases';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';

interface IAttachmentsTableCard {
  data: ITableDataSource;
  downloadAll: (typeUniqueId: string, name: string) => void;
  uploadDocument: (id: string, name: string) => void;
  downloadDocument: (fileId: string, filename: string) => void;
  deleteDocument: (id: string) => void;
  isReadOnly?: boolean;
  isSupervisor?: boolean;
  isAssignUser?: boolean;
}

export const AttachmentsTableCard = ({
  data,
  downloadAll,
  uploadDocument,
  downloadDocument,
  deleteDocument,
  isReadOnly,
  isSupervisor,
  isAssignUser,
}: IAttachmentsTableCard) => {
  const { t } = useTranslations();

  return (
    <StyledTableCard container>
      <StyledTableCardItem size={{ xs: 12 }}>
        <Typography variant="subtitle2">{t(Elabels.documentName)}</Typography>
        <Typography variant="body2">{data.name}</Typography>
      </StyledTableCardItem>
      <StyledTableCardItem size={{ xs: 12 }}>
        <Typography variant="subtitle2">{t(Elabels.importanceNotice)}</Typography>
        <Typography variant="body2">{data.importantNotice}</Typography>
      </StyledTableCardItem>
      <StyledTableCardItem size={{ xs: 12 }}>
        <Typography variant="subtitle2">{t(Elabels.fileName)}</Typography>
        <Typography variant="body2">
          <Grid sx={{ gap: '2px' }}>
            {!data.attachments.length && (
              <Typography
                variant="body2"
                style={{ fontStyle: 'italic', color: colors.gray400 }}
              >
                {t(Elabels.noFilesAdded)}
              </Typography>
            )}
            <Grid style={{ display: 'grid' }} sx={{ gap: '4px' }}>
              {data.attachments.map((item: IAttachment) => (
                <Grid style={{ display: 'flex' }} sx={{ gap: 1 }}>
                  <Link
                    onClick={() => downloadDocument(item.uniqueId, item.name)}
                    underline="none"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      cursor: 'pointer',
                      width: 'fit-content',
                      gap: '8px',
                    }}
                  >
                    {`${item.name}.${item.extension}`}
                    <Icon type={EIconTypes.downloadSmall} fontSize={20} />
                  </Link>
                  {!isReadOnly && (isAssignUser || isSupervisor) && (
                    <Link
                      onClick={() => deleteDocument(item.uniqueId)}
                      underline="none"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        cursor: 'pointer',
                        width: 'fit-content',
                        gap: '8px',
                      }}
                    >
                      <Icon type={EIconTypes.delete} fontSize={18} />
                    </Link>
                  )}
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Typography>
      </StyledTableCardItem>
      <StyledTableCardItem>
        <Grid container spacing={1}>
          <Button
            variant={EButtonVariants.outlined}
            label={t(Elabels.downloadAll)}
            onClick={() => downloadAll(data.typeUniqueId, data.name)}
            disabled={!data.attachments.length}
            size={EButtonSizes.small}
            id="attachments_table_card_download-button"
          />
          {(!isReadOnly || isSupervisor) && (
            <Button
              variant={EButtonVariants.outlined}
              label={t(Elabels.uploadDocument)}
              onClick={() => uploadDocument(data.id as string, data.name)}
              size={EButtonSizes.small}
              id="attachments_table_card_upload-button"
            />
          )}
        </Grid>
      </StyledTableCardItem>
    </StyledTableCard>
  );
};
